/**
 * Spoken progress for topic checks (NM-92).
 *
 * A check takes seconds to minutes against a real provider, and NM-19 removed the
 * per-topic countdown dial and its "checking…" state — so after adding a topic
 * nothing told the user "we're looking now", and a screen-reader user, who cannot
 * see the row's status change, got nothing at all. This module turns the
 * `checking` set on `/api/state` into a line for a polite live region: a topic
 * *entering* the in-flight set says it is being checked, and one *leaving* it
 * reports what the check turned up.
 *
 * A pure fold over successive state snapshots, its own module for the same reason
 * `notifications.ts` is: the transition logic — seed silently, then diff — only
 * runs against a live poll inside the app, so a branch here only ever gets tested
 * through a browser unless it is lifted out. It is the same shape as `noteState`,
 * and tested the same way.
 *
 * **Not the busy dial's return** (NM-19). This is one quiet status line and one
 * spoken sentence, both driven by real in-flight state — not a per-topic animated
 * timer counting down to the next scheduled check.
 */

/**
 * Only the three fields of `/api/state` this reads, structurally — a full
 * `StateResp` satisfies it, and so does a minimal test fixture, without a cast.
 */
interface CheckState {
  checking: readonly string[];
  topics: readonly { id: string; name: string }[];
  todayByTopic: Record<string, number>;
}

/**
 * Topic ids observed in-flight on the previous snapshot. `null` until the first
 * call, so checks already running when the app opens are seeded silently rather
 * than announced as if the user had just started them — the same seed-on-first
 * rule `notifications.ts` uses for its seen-set.
 */
let prevChecking: Set<string> | null = null;

/**
 * Stories-found-today per topic as of the previous snapshot, so a completion can
 * report how many the check turned up: a new story is found *now*, so it lands in
 * today's count, and the delta across the check is what that check produced.
 */
let prevTodayByTopic: Record<string, number> = {};

function storiesPhrase(found: number): string {
  return found === 1 ? '1 new story' : `${String(found)} new stories`;
}

/**
 * Fold a state snapshot into the announcer, returning the text to place in the
 * polite live region this tick, or `''` for nothing to say.
 *
 * Completion is preferred over start when both happen in one tick: a single
 * polite update should carry the more useful half, and "N new stories" is what
 * the user is waiting to hear. Only topics still present in `topics` are named —
 * a check whose topic was deleted mid-flight is dropped rather than announced
 * against a missing name.
 */
export function noteCheckState(state: CheckState): string {
  const nowChecking = new Set(state.checking);
  const today = state.todayByTopic;
  const nameById = new Map(state.topics.map((t) => [t.id, t.name]));

  if (prevChecking === null) {
    prevChecking = nowChecking;
    prevTodayByTopic = { ...today };
    return '';
  }

  const before = prevChecking;
  const completed = [...before].filter((id) => !nowChecking.has(id) && nameById.has(id));
  const started = [...nowChecking].filter((id) => !before.has(id) && nameById.has(id));

  let message = '';
  if (completed.length > 0) {
    message = completed
      .map((id) => {
        const found = Math.max(0, (today[id] ?? 0) - (prevTodayByTopic[id] ?? 0));
        const name = nameById.get(id) ?? 'A topic';
        return found === 0 ? `${name}: no new stories` : `${name}: ${storiesPhrase(found)}`;
      })
      .join('. ');
  } else if (started.length > 0) {
    const only = started.at(0);
    message =
      started.length === 1 && only !== undefined
        ? `Checking ${nameById.get(only) ?? 'a topic'} for new stories…`
        : `Checking ${String(started.length)} topics for new stories…`;
  }

  prevChecking = nowChecking;
  prevTodayByTopic = { ...today };
  return message;
}

/** Tests only: forget the seeded snapshot so each case starts clean. */
export function __resetCheckAnnounceForTests(): void {
  prevChecking = null;
  prevTodayByTopic = {};
}
